import { IconProp } from '@fortawesome/fontawesome-svg-core'
import {
  IconDefinition,
  faExternalLink,
  faLink,
  faLinkSimple,
  faTimes,
  faUnlink,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Editor } from '@tiptap/core'
import { BubbleMenu } from '@tiptap/react'
import { useFormik } from 'formik'
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { LoadingRow } from '../../LoadingRows/LoadingRow'
import { Tooltip } from '../../Tooltip/Tooltip'
import { TruncatedText } from '../../TruncatedText/TruncatedText'
import { hideAll } from 'tippy.js'

export interface ILinkSelectOption {
  url: string
  title: string
  description?: string
  icon?: IconDefinition | IconProp
}

export interface ILinkOptions {
  color?: string
  options?: ILinkSelectOption[]
  error?: any
  isLoading?: boolean
  isFetchingNextPage?: boolean
  hasNextPage?: boolean
  onInputChange?: (search: string) => void
  onMenuScrollToBottom?: VoidFunction
}

interface IProps {
  editor: Editor | null
  linkOptions?: ILinkOptions
  disabled?: boolean
}

export const EditorLinkBubbleMenu = ({ editor, linkOptions, disabled }: IProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(undefined)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const { options = [], onInputChange: handleInputChange } = linkOptions || {}
  const isLink = editor?.isActive('link')
  const url: string = editor?.state?.tr?.selection?.$anchor?.marks()?.[0]?.attrs?.href || ''
  // TODO: Add text editing later
  const { values, handleChange, setValues } = useFormik<{ url: string /* text: string */ }>({
    initialValues: {
      url,
      // TODO: Add text editing later
      // text: editor?.getAttributes('link').innerText || '',
    },
    onSubmit: () => {},
  })
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const ref = useRef<HTMLDivElement>(null)
  const unblur = useCallback(() => {
    if (editor?.isActive('link')) {
      hideAll()
    } else {
      setIsEditing(false)
    }
  }, [editor])

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        unblur()
      }
    }

    const handleClickOutside = (e: any) => {
      if (!ref.current?.contains(e.target)) {
        unblur()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', down)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', down)
    }
  }, [unblur, ref])

  useEffect(() => {
    setValues({
      url,
      // TODO: Add text editing later
      // text: editor?.getAttributes('link').innerText || '',
    })
  }, [url, setValues])

  const setLink = (url: string) => {
    if (!editor) return

    const href = ['http:', 'https:', 'sms:', 'tel:', 'mailto:'].some((protocol) =>
      url.startsWith(protocol),
    )
      ? url
      : 'http://' + url
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href, target: '_blank' })
      .setTextSelection(editor.view.state.selection.to)
      .run()
  }

  const handleKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
    if (!editor) return
    if (evt.key === 'Escape') {
      unblur()
    }
    if (evt.key === 'ArrowUp') {
      const currentIndex = typeof selectedIndex !== 'undefined' ? selectedIndex : 1
      setSelectedIndex((currentIndex + options.length - 1) % options.length)
      const selectedEl = containerRef.current?.children[currentIndex]
      if (selectedEl && 'offsetTop' in selectedEl)
        containerRef.current?.scrollTo({
          top: Number(selectedEl?.offsetTop) + Number(selectedEl?.clientHeight),
        })
      return true
    }

    if (evt.key === 'ArrowDown') {
      const currentIndex = typeof selectedIndex !== 'undefined' ? selectedIndex : -1
      setSelectedIndex((currentIndex + 1) % options.length)
      const selectedEl = containerRef.current?.children[currentIndex]
      if (selectedEl && 'offsetTop' in selectedEl)
        containerRef.current?.scrollTo({ top: Number(selectedEl?.offsetTop) })
      return true
    }

    if (evt.key === 'Enter' && selectedIndex !== undefined) {
      setLink(options[selectedIndex]?.url)
      return true
    }
    if (evt.key === 'Enter') {
      evt.preventDefault()
      if (values.url) {
        setLink(values.url)
        setIsEditing(false)
      } else {
        editor.chain().focus().extendMarkRange('link').unsetLink().run()
      }
    }
  }

  if (!editor || disabled) return null
  if (isLink && (isEditing || !url)) {
    return (
      <BubbleMenu
        className="bg-body border rounded-xs p-2"
        tippyOptions={{
          duration: 150,
          zIndex: 1060,
          placement: 'bottom',
        }}
        editor={editor}
        shouldShow={({ editor }) => editor.isActive('link')}
      >
        <div ref={ref}>
          <div className="input-group">
            <span className="input-group-text border-end-0">
              <FontAwesomeIcon fixedWidth={true} icon={faLink} />
            </span>
            <input
              ref={inputRef}
              type="text"
              name="url"
              placeholder={handleInputChange ? 'Paste or search a link...' : 'Paste a link...'}
              className="form-control border-start-0 border-end-0"
              value={values.url}
              autoFocus={true}
              autoComplete="off"
              onKeyDown={handleKeyDown}
              onChange={(e) => {
                handleChange(e)
                handleInputChange?.(e.target.value)
              }}
            />
            <button
              className="input-group-text border-start-0"
              type="button"
              onClick={() => setValues((v) => ({ ...v, url: '' }))}
            >
              <FontAwesomeIcon fixedWidth={true} icon={faTimes} />
            </button>
          </div>
          {/*  TODO: Add text editing later */}
          {/* <div className="input-group">
                <span className="input-group-text border-end-0 bg-white">
                  <FontAwesomeIcon fixedWidth={true} icon={faAlignLeft} />
                </span>
                <input
                  type="text"
                  name="text"
                  className="form-control border-start-0"
                  value={values.text}
                  autoComplete="off"
                  onKeyDown={handleKeyDown}
                  onChange={handleChange}
                />
              </div> */}
          {linkOptions && (
            <SearchLink
              selectedIndex={selectedIndex}
              containerRef={containerRef}
              linkOptions={linkOptions}
              onSelect={(url) => {
                setLink(url)
                setIsEditing(false)
              }}
            />
          )}
        </div>
      </BubbleMenu>
    )
  }
  return (
    <BubbleMenu
      className="bg-body border rounded-xs p-2"
      tippyOptions={{
        duration: 150,
        zIndex: 1060,
        placement: 'bottom',
      }}
      editor={editor}
      shouldShow={({ editor }) => editor.isActive('link')}
    >
      <div ref={ref}>
        <button type="button" className="btn" onClick={() => setIsEditing(true)}>
          Edit link
        </button>
        <span className="px-3 pb-0">
          <span className="btn-separator"></span>
        </span>
        <a className="btn" target="_blank" rel="noreferrer" href={values.url}>
          <Tooltip title="Open link in new tab" placement="top">
            <FontAwesomeIcon icon={faExternalLink} fixedWidth={true} />
          </Tooltip>
        </a>
        <button
          type="button"
          onClick={() => editor.chain().focus().extendMarkRange('link').unsetLink().run()}
          className="btn"
        >
          <Tooltip title="Unlink" placement="top">
            <FontAwesomeIcon icon={faUnlink} fixedWidth={true} />
          </Tooltip>
        </button>
      </div>
    </BubbleMenu>
  )
}

interface ISearchLinkProps {
  linkOptions: ILinkOptions
  containerRef?: React.RefObject<HTMLDivElement>
  selectedIndex?: number
  onSelect: (url: string) => void
}

export const SearchLink = ({
  linkOptions,
  containerRef,
  selectedIndex,
  onSelect: handleSelect,
}: ISearchLinkProps) => {
  const {
    options = [],
    error,
    isLoading = false,
    isFetchingNextPage = false,
    hasNextPage = false,
    onMenuScrollToBottom: fetchNextPage = () => {},
  } = linkOptions || {}
  const [infiniteRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage,
    onLoadMore: fetchNextPage,
    disabled: !!error,
  })

  return (
    <div className="pt-3 pb-1">
      {options.length > 0 && (
        <div
          ref={containerRef}
          style={{ maxHeight: '15rem', overflowY: 'auto', overflowX: 'hidden' }}
        >
          {options.map(({ title, description, url, icon }, i) => (
            <Fragment key={['loading-row', i].join('-')}>
              <hr className="my-1" />
              <button
                type="button"
                className={`dropdown-item px-0 ${selectedIndex === i ? 'active' : ''}`.trim()}
                onClick={() => {
                  handleSelect(url)
                }}
              >
                <div className="d-flex w-100">
                  <div className="me-2" style={{ width: '1.25rem' }}>
                    <FontAwesomeIcon icon={icon || faLinkSimple} fixedWidth={true} />
                  </div>
                  <div style={{ width: 'calc(100% - 1.25rem - 0.5rem)' }}>
                    <div>{title}</div>
                    <TruncatedText className="text-body-secondary small">
                      {description}
                    </TruncatedText>
                  </div>
                </div>
              </button>
            </Fragment>
          ))}
          <div ref={infiniteRef}>{isFetchingNextPage && hasNextPage && <LoadingLinks />}</div>
        </div>
      )}
      {isLoading && <LoadingLinks />}
    </div>
  )
}

const LoadingLinks = () => (
  <>
    {Array.from({ length: 3 })
      .fill(null)
      .map((_, i) => (
        <Fragment key={['loading-row', i].join('-')}>
          <hr className={i === 0 ? 'my-3' : 'mb-3'} />
          <div className="d-flex w-100">
            <div className="me-2" style={{ width: '1.25rem' }}>
              <LoadingRow
                colProps={{ style: { height: '1.25rem' } }}
                animateProps={{ style: { height: '1rem', width: '1rem' } }}
              />
            </div>
            <div style={{ width: 'calc(100% - 1.25rem)' }}>
              <LoadingRow
                colProps={{ style: { height: '1.25rem' } }}
                animateProps={{ style: { height: '1rem' } }}
              />
              <LoadingRow
                colProps={{ className: 'col-10', style: { height: '0.75rem' } }}
                animateProps={{ style: { height: '0.5rem' } }}
              />
            </div>
          </div>
        </Fragment>
      ))}
  </>
)
